import React, { useCallback, useEffect, useState } from 'react';
import {
  createEntry,
  deleteMedia,
  fetchCategories,
  fetchEntries,
  fetchMembers,
  getSession,
  onAuthChange,
  saveRating,
  signOut,
  signMediaUrls,
  uploadMedia,
} from '../lib/helenApi';
import SignIn from '../components/helen/SignIn';
import TicketStub from '../components/helen/TicketStub';
import EntryForm from '../components/helen/EntryForm';
import '../components/helen/helen.css';

const Helen = () => {
  // undefined while we are still asking Supabase; null once we know nobody is in.
  const [session, setSession] = useState(undefined);
  const [members, setMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [mediaUrls, setMediaUrls] = useState({});
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    let alive = true;
    getSession().then((s) => alive && setSession(s ?? null));
    const unsubscribe = onAuthChange((s) => alive && setSession(s ?? null));
    return () => {
      alive = false;
      unsubscribe();
    };
  }, []);

  const viewerId = session?.user?.id ?? null;

  const load = useCallback(async () => {
    try {
      const [loadedMembers, loadedCategories, loadedEntries] = await Promise.all([
        fetchMembers(),
        fetchCategories(),
        fetchEntries(),
      ]);
      setMembers(loadedMembers);
      setCategories(loadedCategories);
      setEntries(loadedEntries);
      setLoadError(null);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (viewerId) load();
  }, [viewerId, load]);

  // Private bucket: every photo and clip needs a fresh signed URL after each load.
  useEffect(() => {
    const paths = entries.flatMap((e) => (e.photos || []).map((p) => p.storage_path));
    if (!paths.length) return undefined;

    let alive = true;
    signMediaUrls(paths).then((urls) => alive && setMediaUrls(urls));
    return () => {
      alive = false;
    };
  }, [entries]);

  // Every write re-reads the feed, which is also how a freshly revealed score
  // arrives: the row was withheld by RLS until the moment yours landed.
  const run = useCallback(
    async (work) => {
      try {
        setActionError(null);
        await work();
        await load();
      } catch (err) {
        setActionError(err.message);
      }
    },
    [load]
  );

  if (session === undefined) return <div className="helen-booting" aria-hidden="true" />;
  if (!session) return <SignIn onSignedIn={setSession} />;

  const me = members.find((m) => m.user_id === viewerId) || null;
  const them = members.find((m) => m.user_id !== viewerId) || null;
  const categoryBySlug = Object.fromEntries(categories.map((c) => [c.slug, c]));

  return (
    <div className="helen">
      <header className="helen-header" data-testid="helen-top">
        <button type="button" className="helen-button" onClick={() => setAdding((v) => !v)}>
          Add something
        </button>
        <button type="button" className="helen-linkbutton" onClick={signOut}>
          Sign out
        </button>
      </header>

      {!loaded && !loadError && <p className="helen-loading">Fetching the stubs…</p>}

      {adding && (
        <EntryForm
          categories={categories}
          onCancel={() => setAdding(false)}
          onAdd={(fields) =>
            run(async () => {
              await createEntry({ ...fields, userId: viewerId });
              setAdding(false);
            })
          }
        />
      )}

      {(loadError || actionError) && (
        <p className="helen-error" role="alert">
          {loadError || actionError}
        </p>
      )}

      {loaded && !entries.length && !loadError && (
        <p className="helen-empty">
          Nothing here yet. Add the last thing you two did together and give it a number.
        </p>
      )}

      <div className="helen-feed">
        {entries.map((entry) => (
          <TicketStub
            key={entry.id}
            entry={entry}
            category={categoryBySlug[entry.category]}
            me={me}
            them={them}
            viewerId={viewerId}
            mediaUrls={mediaUrls}
            onSaveRating={(entryId, { score, note }) =>
              run(() => saveRating({ entryId, userId: viewerId, score, note }))
            }
            onUploadMedia={(entryId, file) =>
              run(() => uploadMedia({ entryId, userId: viewerId, file }))
            }
            onDeleteMedia={(item) => run(() => deleteMedia(item))}
          />
        ))}
      </div>

    </div>
  );
};

export default Helen;
